import { format } from 'date-fns';
import googleMeetIcon from '@/assets/images/appointments/google_meet.svg';
import type { AppointmentAvailableSlot } from '@/lib/api/appointments';
import type { Appointment } from '@/types';
import type { Client as ClientType } from '@/types/client';
import { formatInTimezone } from '@/utils/timezone-utils';
import { formatDelimitedDisplayLabel, formatDisplayLabel } from '@/utils/display-labels';
import { getAppointmentSchedulingTimezone, getIanaTimeZone } from '@/utils/appointments/appointment-timezone';

export type ScheduleMeetingType = 'online' | 'in_person';
export type SchedulePlatform = 'zoom' | 'google_meet' | 'microsoft_teams';

export const weekDayLabels = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
export const scheduleDurationOptions = [15, 30, 45, 60] as const;
export const onlinePlatformOptions: Array<{
  id: SchedulePlatform;
  label: string;
  icon: string;
  iconAlt: string;
}> = [
  { id: 'google_meet', label: 'Google Meet', icon: googleMeetIcon, iconAlt: 'Google Meet' },
];

export type SchedulePrefillFromAppointment = {
  clientId: string;
  title: string;
  description: string;
  date: Date;
  time: string;
  durationMinutes: number;
  meetingType: ScheduleMeetingType;
  inPersonAddress: string;
};

export function getAppointmentStartDate(appointment: Appointment): Date {
  const raw = appointment as unknown as Record<string, unknown>;
  const startSrc = raw.startTime ?? raw.start_time ?? appointment.date;
  return startSrc instanceof Date ? startSrc : new Date(String(startSrc));
}

/** HH:mm for slot pickers — must match GET /appointments/available-slots timezone. */
export function getAppointmentSlotTimeHHmm(
  appointment: Appointment,
  timezone: string = getAppointmentSchedulingTimezone(),
): string {
  return formatInTimezone(getAppointmentStartDate(appointment), timezone, 'HH:mm');
}

export function getSchedulePrefillFromAppointment(
  appointment: Appointment,
  timezone: string = getIanaTimeZone(),
): SchedulePrefillFromAppointment {
  const raw = appointment as unknown as Record<string, unknown>;
  const start = getAppointmentStartDate(appointment);
  const slotTimezone = timezone;

  const endSrc = raw.endTime ?? raw.end_time;
  let durationMinutes = Number(appointment.duration);
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
    if (endSrc) {
      const end = endSrc instanceof Date ? endSrc : new Date(String(endSrc));
      durationMinutes = Math.max(15, Math.round((end.getTime() - start.getTime()) / 60_000));
    } else {
      durationMinutes = 30;
    }
  }

  const type = String(raw.appointmentType ?? raw.mode ?? appointment.mode ?? '').toLowerCase();
  const location = String(raw.location ?? '').trim();
  const isInPerson =
    type === 'offline' || type === 'in_person' || location.toLowerCase().includes('person');

  return {
    clientId: String(raw.clientId ?? raw.client_id ?? appointment.clientId ?? '').trim(),
    title: String(raw.title ?? appointment.type ?? '').trim(),
    description: String(raw.description ?? appointment.notes ?? '').trim(),
    date: start,
    time: getAppointmentSlotTimeHHmm(appointment, slotTimezone),
    durationMinutes,
    meetingType: isInPerson ? 'in_person' : 'online',
    inPersonAddress: isInPerson ? location : '',
  };
}

/** Client label fields sometimes returned on GET /appointments/:id or list rows. */
export function getClientDisplayFromAppointment(appointment: Appointment): {
  name: string;
  email: string;
} {
  const raw = appointment as unknown as Record<string, unknown>;
  const name = String(raw.clientName ?? raw.client_name ?? '').trim();
  const email = String(raw.clientEmail ?? raw.client_email ?? '').trim();
  return { name: name || 'Client', email };
}

/** e.g. `9:00 AM` from API slot start/end (24h HH:mm). */
export function formatAvailableSlotLabel(slot: Pick<AppointmentAvailableSlot, 'startTime' | 'endTime'>): string {
  const [startHours, startMinutes] = slot.startTime.split(':').map(Number);
  const start = new Date(2000, 0, 1, startHours, startMinutes || 0);
  return format(start, 'h:mm a');
}

/** e.g. `9:00 AM` for a 30-minute session starting at 09:00. */
export function formatScheduleSlotLabel(startHHmm: string, durationMinutes: number): string {
  const [hours, minutes] = startHHmm.split(':').map(Number);
  const start = new Date(2000, 0, 1, hours, minutes || 0);
  return format(start, 'h:mm a');
}

export function addMinutesToHHmm(start: string, addMinutes: number): string {
  const [h, m] = start.split(':').map(Number);
  const totalMinutes = h * 60 + m + addMinutes;
  const wrapped = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const nh = Math.floor(wrapped / 60);
  const nm = wrapped % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

export { getAppointmentSchedulingTimezone, getAvailabilitySaveTimezone, getIanaTimeZone, resolveNutritionistSchedulingTimezone } from './appointment-timezone';

export function getClientDisplayName(client?: ClientType): string {
  if (!client) return 'Unknown Client';

  const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim();
  if (fullName) return fullName;
  if (typeof client.fullName === 'string' && client.fullName.trim()) return client.fullName.trim();

  return client.email || 'Unknown Client';
}

export function getClientConditionSummary(client?: ClientType): string {
  if (!client) return '';

  const conditionNames = (client.medicalConditionDetails ?? [])
    .map((condition) => formatDisplayLabel(condition.conditionName))
    .filter((conditionName): conditionName is string => Boolean(conditionName));

  if (conditionNames.length > 0) {
    return Array.from(new Set(conditionNames)).slice(0, 2).join(', ');
  }

  const medicalConditions = typeof client.medicalConditions === 'string' ? client.medicalConditions.trim() : '';
  return medicalConditions ? formatDelimitedDisplayLabel(medicalConditions) : '';
}

export function getClientStatusLabel(client?: ClientType): string {
  const rawStatus = String(client?.status ?? '').trim().toLowerCase();
  if (!rawStatus || rawStatus === 'active') return 'Active';
  if (!['inactive', 'suspended', 'archived'].includes(rawStatus)) return 'Active';

  return formatDisplayLabel(rawStatus);
}

export function mergeClientsById(prev: ClientType[], incoming: ClientType[]): ClientType[] {
  const map = new Map<string, ClientType>();
  for (const c of prev) {
    map.set(c.id.toString(), c);
  }
  for (const c of incoming) {
    map.set(c.id.toString(), c);
  }
  return Array.from(map.values());
}
